import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { InternalTag, InternalTagType } from '../../../types/InternalTag'
import { BrandMutation, InternalBrand } from '../types/InternalBrand'

export type BrandTagChip = { id: string; name: string; known: boolean }

export type BrandRow = {
  id: string
  brand: InternalBrand
  name: string
  owner: string
  tags: BrandTagChip[]
}

export type BrandFormValues = {
  names: ContentLanguageTranslation
  owner: string
  tagIds: string[]
}

export type BrandField = 'name_en' | 'name_ja' | 'name_zh' | 'owner' | 'tags'

export const brandFieldLabelKeys: Record<BrandField, string> = {
  name_en: 'internal-analysts:brands_field_name_en',
  name_ja: 'internal-analysts:brands_field_name_ja',
  name_zh: 'internal-analysts:brands_field_name_zh',
  owner: 'internal-analysts:brands_owner',
  tags: 'internal-analysts:brands_tags',
}

// Plain code-unit comparison, as the legacy `orderBy`.
const byCodeUnit = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

export const getBrandTagOptions = (tags: InternalTag[]): InternalTag[] =>
  tags.filter((tag) => tag.type === InternalTagType.Brand).sort((a, b) => byCodeUnit(a.name, b.name))

export const toBrandTagChips = (tagIds: string[], tags: InternalTag[]): BrandTagChip[] => {
  const namesById = new Map(tags.map((tag) => [tag.id, tag.name]))
  return tagIds.map((id) => {
    const name = namesById.get(id)
    return { id, name: name ?? id, known: name !== undefined }
  })
}

export const buildBrandRows = (brands: InternalBrand[], tags: InternalTag[]): BrandRow[] =>
  brands.map((brand) => ({
    id: brand.id,
    brand,
    name: brand.names?.en ?? '',
    owner: brand.owner ?? '',
    tags: toBrandTagChips(brand.tags ?? [], tags),
  }))

export const toBrandFormValues = (brand: InternalBrand | undefined): BrandFormValues => ({
  names: { en: brand?.names?.en ?? '', ja: brand?.names?.ja ?? '', zh: brand?.names?.zh ?? '' },
  owner: brand?.owner ?? '',
  tagIds: brand?.tags ?? [],
})

// Legacy's bodies: an edit sends the stored brand with the edited fields, a create only the edited fields.
export const toBrandMutation = (original: InternalBrand | undefined, values: BrandFormValues): BrandMutation => {
  const names = { en: values.names.en ?? '', ja: values.names.ja ?? '', zh: values.names.zh ?? '' }
  if (!original) return { names, owner: values.owner || null, tags: values.tagIds }
  const owner = values.owner === (original.owner ?? '') ? original.owner ?? null : values.owner
  return { ...original, names, owner, tags: values.tagIds }
}

export const getUnstoredBrandFields = (sent: BrandMutation, stored: InternalBrand): BrandField[] => {
  const unstored: BrandField[] = []
  if ((stored.names?.en ?? '') !== sent.names.en) unstored.push('name_en')
  if ((stored.names?.ja ?? '') !== sent.names.ja) unstored.push('name_ja')
  if ((stored.names?.zh ?? '') !== sent.names.zh) unstored.push('name_zh')
  if ((stored.owner ?? '') !== (sent.owner ?? '')) unstored.push('owner')
  if ((stored.tags ?? []).join() !== sent.tags.join()) unstored.push('tags')
  return unstored
}
